import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  ArrowLeft, 
  Image, 
  ShoppingCart, 
  Plus, 
  Check, 
  Palette,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PWAGalleryView = () => {
  const { accessCode } = useParams();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [backgrounds, setBackgrounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);
  const [cart, setCart] = useState([]);
  const [showProductSheet, setShowProductSheet] = useState(false);
  const [selectedProductPhoto, setSelectedProductPhoto] = useState(null);

  useEffect(() => {
    fetchGalleryData();
  }, [accessCode]);

  const fetchGalleryData = async () => {
    try {
      const response = await axios.get(`${API}/client/gallery/${accessCode}`);
      setGallery(response.data.gallery);
      setPhotos(response.data.photos);
      setProducts(response.data.products);
      setPackages(response.data.packages);
      setBackgrounds(response.data.backgrounds || []);
      
      // Save to recent galleries
      const recent = JSON.parse(localStorage.getItem('recentGalleries') || '[]');
      const newRecent = [
        { code: accessCode, name: response.data.gallery.name, timestamp: Date.now() },
        ...recent.filter(r => r.code !== accessCode)
      ].slice(0, 5);
      localStorage.setItem('recentGalleries', JSON.stringify(newRecent));
    } catch (error) {
      toast.error('Gallery not found');
      navigate('/app');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoClick = (photo, index) => {
    setSelectedPhoto({ ...photo, index });
    setShowPhotoViewer(true);
  };

  const handlePrevPhoto = () => {
    if (selectedPhoto && selectedPhoto.index > 0) {
      const newIndex = selectedPhoto.index - 1;
      setSelectedPhoto({ ...photos[newIndex], index: newIndex });
    }
  };

  const handleNextPhoto = () => {
    if (selectedPhoto && selectedPhoto.index < photos.length - 1) {
      const newIndex = selectedPhoto.index + 1;
      setSelectedPhoto({ ...photos[newIndex], index: newIndex });
    }
  };

  const handleAddToCart = (photo) => {
    setSelectedProductPhoto(photo);
    setShowProductSheet(true);
  };

  const addProductToCart = (product, photo) => {
    const cartItem = {
      id: Date.now(),
      product,
      photo,
      background: null,
      quantity: 1
    };
    setCart(prev => [...prev, cartItem]);
    toast.success(`Added ${product.name} to cart`);
    setShowProductSheet(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="px-4 pt-6 text-center">
        <p className="text-stone-500">Gallery not found</p>
        <Button onClick={() => navigate('/app')} className="mt-4">
          Go Home
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-stone-200 safe-area-pt">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/app')}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-medium text-stone-800 truncate">{gallery.name}</h1>
            <p className="text-xs text-stone-500">{photos.length} photos</p>
          </div>
          {cart.length > 0 && (
            <Button 
              className="btn-action relative"
              onClick={() => navigate(`/app/checkout/${accessCode}`)}
            >
              <ShoppingCart className="h-5 w-5" />
              <Badge className="absolute -top-2 -right-2 bg-amber-600 text-white text-xs px-1.5">
                {cart.length}
              </Badge>
            </Button>
          )}
        </div>
      </div>

      {/* Photo Grid */}
      <div className="p-2">
        <div className="grid grid-cols-3 gap-1">
          {photos.map((photo, index) => (
            <div 
              key={photo.id}
              className="aspect-square relative group cursor-pointer overflow-hidden rounded-sm bg-stone-200"
              onClick={() => handlePhotoClick(photo, index)}
            >
              <img
                src={photo.thumbnail_url || photo.url}
                alt={photo.filename}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-active:bg-black/20 transition-colors" />
            </div>
          ))}
        </div>
      </div>

      {/* Photo Viewer Modal */}
      <Dialog open={showPhotoViewer} onOpenChange={setShowPhotoViewer}>
        <DialogContent className="max-w-full h-full p-0 bg-black border-none">
          <div className="relative h-full flex flex-col">
            {/* Close Button */}
            <div className="absolute top-4 right-4 z-50">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setShowPhotoViewer(false)}
                className="text-white hover:bg-white/20"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>

            {/* Navigation */}
            {selectedPhoto && selectedPhoto.index > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrevPhoto}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20"
              >
                <ChevronLeft className="h-8 w-8" />
              </Button>
            )}
            {selectedPhoto && selectedPhoto.index < photos.length - 1 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNextPhoto}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20"
              >
                <ChevronRight className="h-8 w-8" />
              </Button>
            )}

            {/* Photo */}
            <div className="flex-1 flex items-center justify-center p-4">
              {selectedPhoto && (
                <img
                  src={selectedPhoto.url}
                  alt={selectedPhoto.filename}
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </div>

            {/* Actions */}
            <div className="p-4 bg-gradient-to-t from-black/80 to-transparent">
              <div className="flex gap-3">
                <Button 
                  className="flex-1 btn-action"
                  onClick={() => {
                    setShowPhotoViewer(false);
                    handleAddToCart(selectedPhoto);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add to Order
                </Button>
              </div>
              <p className="text-center text-white/60 text-xs mt-2">
                {selectedPhoto?.index + 1} of {photos.length}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Selection Sheet */}
      <Dialog open={showProductSheet} onOpenChange={setShowProductSheet}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Product</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Packages */}
            {packages.length > 0 && (
              <div>
                <h3 className="font-medium text-stone-700 mb-2">Packages</h3>
                <div className="space-y-2">
                  {packages.map(pkg => (
                    <Card 
                      key={pkg.id}
                      className="border-stone-200 cursor-pointer hover:border-amber-300 transition-colors"
                      onClick={() => addProductToCart(pkg, selectedProductPhoto)}
                    >
                      <CardContent className="p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-stone-800">{pkg.name}</p>
                          <p className="text-sm text-stone-500">{pkg.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-amber-600">${pkg.price.toFixed(2)}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Products */}
            {products.length > 0 && (
              <div>
                <h3 className="font-medium text-stone-700 mb-2">Individual Products</h3>
                <div className="space-y-2">
                  {products.map(product => (
                    <Card 
                      key={product.id}
                      className="border-stone-200 cursor-pointer hover:border-amber-300 transition-colors"
                      onClick={() => addProductToCart(product, selectedProductPhoto)}
                    >
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {product.allows_background && (
                            <Palette className="h-4 w-4 text-stone-400" />
                          )}
                          <div>
                            <p className="font-medium text-stone-800">{product.name}</p>
                            <p className="text-sm text-stone-500">{product.category}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-amber-600">${product.price.toFixed(2)}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating Cart Button */}
      {cart.length > 0 && (
        <div className="fixed bottom-24 left-0 right-0 px-4 z-40">
          <Button 
            className="w-full btn-action shadow-lg"
            onClick={() => navigate(`/gallery/${accessCode}`)}
          >
            <ShoppingCart className="h-5 w-5 mr-2" />
            View Cart ({cart.length} items)
          </Button>
        </div>
      )}
    </div>
  );
};

export default PWAGalleryView;
